import { useState } from 'react';
import { useAsync, useBookingTracking } from '../../../../hooks';
import { useSearchParams } from 'react-router-dom';
import BookingService from '../../../../services/booking/BookingService';
import { formatDateToLongUsWeekdayLocal, to12HourTime, trackBookingCancelled } from '../../../../utils';
import { SpinnerDefault } from '../../../../components';
import { motion } from 'framer-motion';
import './BookingCancelPage.scss';
import { LocationCard, CustomerCard } from '../../components';

export function BookingCancelPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const bookingService = new BookingService();

  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const { value: preview, loading, error } = useAsync(async () => {
    if (!token) return null;
    return await bookingService.getCancelPreviewAsync(token);
  }, [token]);

  useBookingTracking(preview?.location?.locationCode);

  const handleCancel = async () => {
    setCancelling(true);
    setCancelError(null);
    try {
      await bookingService.cancelByTokenAsync(token);
      trackBookingCancelled(preview);
      setCancelled(true);
    } catch (e: any) {
      setCancelError(e?.message || 'Something went wrong. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const { isPast, isCancellable } = (() => {
    if (!preview?.bookingEvents?.length) return { isPast: false, isCancellable: false };
    const earliest = preview.bookingEvents.reduce((min: any, e: any) => {
      const dt = new Date(`${e.date}T${e.startTime}`);
      return dt < new Date(`${min.date}T${min.startTime}`) ? e : min;
    });
    const appointmentTime = new Date(`${earliest.date}T${earliest.startTime}`);
    const now = Date.now();
    const cutoff = new Date(now + 24 * 60 * 60 * 1000);
    return {
      isPast: appointmentTime <= new Date(now),
      isCancellable: appointmentTime > cutoff,
    };
  })();

  if (!token) {
    return (
      <div className="container mt-5 text-center booking-cancel-error">
        <i className="fa fa-exclamation-triangle fa-3x text-warning mb-3"></i>
        <h3>Invalid Link</h3>
        <p className="text-muted">This cancellation link is invalid or has expired.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center mt-5">
        <SpinnerDefault />
      </div>
    );
  }

  if (error || !preview) {
    const err = error as any;
    const code = err?.code;
    const msg = err?.detail;

    if (code === 409) {
      return (
        <div className="container mt-5 text-center booking-cancel-error">
          <div className="booking-cancel-page__icon-circle booking-cancel-page__icon-circle--warning mb-3">
            <i className="fa fa-ban fa-3x"></i>
          </div>
          <h3 className="mb-2">Already Cancelled</h3>
          <p className="text-muted">{msg ?? 'This booking has already been cancelled.'}</p>
        </div>
      );
    }

    return (
      <div className="container mt-5 text-center booking-cancel-error">
        <div className="booking-cancel-page__icon-circle booking-cancel-page__icon-circle--error mb-3">
          <i className="fa fa-times fa-3x"></i>
        </div>
        <h3 className="mb-2">{msg ?? 'Unable to load booking'}</h3>
        <p className="text-muted">This link may have already been used or has expired.</p>
      </div>
    );
  }

  if (isPast) {
    return (
      <div className="container mt-5 text-center booking-cancel-error">
        <div className="booking-cancel-page__icon-circle booking-cancel-page__icon-circle--error mb-3">
          <i className="fa fa-calendar-times fa-3x"></i>
        </div>
        <h3 className="mb-2">Appointment Already Passed</h3>
        <p className="text-muted">This appointment has already taken place and can no longer be cancelled.</p>
      </div>
    );
  }

  if (cancelled) {
    return (
      <motion.div
        initial={{ y: 10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="booking-cancelled-page mt-5"
      >
        {/* Success banner */}
        <div className="text-center mb-4">
          <div className="booking-cancel-page__icon-circle booking-cancel-page__icon-circle--success mb-3">
            <i className="fa fa-check fa-3x"></i>
          </div>
          <h2 className="mb-1">Appointment Cancelled</h2>
          <p className="text-muted">Your appointment has been successfully cancelled.</p>
        </div>

        {/* Cancelled booking summary */}
        <div className="row g-3 mb-3">
          <div className="col-24 col-sm-12">
            <CustomerCard
              firstName={preview.customer?.firstName}
              lastName={preview.customer?.lastName}
              email={preview.customer?.email}
              phone={preview.customer?.phone}
            />
          </div>
          <div className="col-24 col-sm-12">
            <LocationCard
              name={preview.location?.name}
              streetAddress={preview.location?.streetAddress}
              city={preview.location?.city}
              postalCode={preview.location?.postalCode}
              province={preview.location?.region}
            />
          </div>
        </div>

        <div className="p-4 shadow-sm border-0 card">
          <p className="text-muted small text-uppercase fw-semibold mb-3">
            <i className="fa fa-calendar me-2"></i>Cancelled Appointments
          </p>
          {preview.bookingEvents?.map((event: any, i: number) => (
            <div key={event.id ?? i} className={i < preview.bookingEvents.length - 1 ? 'mb-3 pb-3 border-bottom' : ''}>
              <p className="fw-semibold mb-1 text-decoration-line-through text-muted">{event.service?.name}</p>
              {event.provider?.name && (
                <p className="text-muted small mb-1">
                  <i className="fa fa-user me-1"></i>with {event.provider.name}
                </p>
              )}
              {event.date && (
                <p className="text-muted small mb-1">
                  <i className="fa fa-calendar me-1"></i>
                  {formatDateToLongUsWeekdayLocal(event.date)}
                  {event.startTime && <span> at {to12HourTime(event.startTime)}</span>}
                </p>
              )}
            </div>
          ))}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ y: 10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="mt-5 booking-cancel-page"
    >
      {/* Header */}
      <div className="text-center mb-4">
          <div className="booking-cancel-page__icon-circle booking-cancel-page__icon-circle--warning mb-3">
            <i className="fa fa-calendar-times fa-3x"></i>
        </div>
        <h2 className="mb-1">Cancel Appointment</h2>
        <p className="text-muted">Please review your appointment details before cancelling.</p>
      </div>

      {/* Location + Customer */}
      <div className="row g-3 mb-3">
        <div className="col-24 col-sm-12">
          <CustomerCard
            firstName={preview.customer?.firstName}
            lastName={preview.customer?.lastName}
            email={preview.customer?.email}
            phone={preview.customer?.phone}
          />
        </div>
        <div className="col-24 col-sm-12">
          <LocationCard
            name={preview.location?.name}
            streetAddress={preview.location?.streetAddress}
            city={preview.location?.city}
            province={preview.location?.region}
            postalCode={preview.location?.postalCode}
          />
        </div>
      </div>

      {/* Appointments */}
      <div className="mb-4 p-4 shadow-sm border-0 card">
        <p className="text-muted small text-uppercase fw-semibold mb-3">
          <i className="fa fa-calendar me-2"></i>Appointments
        </p>
        {preview.bookingEvents?.map((event: any, i: number) => (
          <div key={event.id ?? i} className={i < preview.bookingEvents.length - 1 ? 'mb-3 pb-3 border-bottom' : ''}>
            <p className="fw-semibold mb-1">{event.service?.name}</p>
            {event.provider?.name && (
              <p className="text-muted small mb-1">
                <i className="fa fa-user me-1"></i>with {event.provider.name}
              </p>
            )}
            {event.date && (
              <p className="text-muted small mb-1">
                <i className="fa fa-calendar me-1"></i>
                {formatDateToLongUsWeekdayLocal(event.date)}
                {event.startTime && <span> at {to12HourTime(event.startTime)}</span>}
              </p>
            )}
          </div>
        ))}
      </div>

      {cancelError && (
        <div className="alert alert-danger mb-3">{cancelError}</div>
      )}

      {/* Actions */}
      {!isCancellable ? (
        <div className="alert alert-warning text-center mb-0">
          <i className="fa fa-clock me-2"></i>
          This appointment can no longer be cancelled online. Please contact us directly.
        </div>
      ) : !confirmed ? (
        <div className="d-flex gap-3">
          <button
            className="btn btn-danger flex-fill"
            onClick={() => setConfirmed(true)}
          >
            Cancel Appointment
          </button>
        </div>
      ) : (
        <div>
          <p className="text-center text-danger fw-semibold mb-3">
            Are you absolutely sure you want to cancel?
          </p>
          <div className="d-flex gap-3">
            <button
              className="btn btn-secondary flex-fill text-white"
              onClick={() => setConfirmed(false)}
              disabled={cancelling}
            >
              No, Keep It
            </button>
            <button
              className="btn btn-danger flex-fill"
              onClick={handleCancel}
              disabled={cancelling}
            >
              {cancelling ? <><span className="spinner-border spinner-border-sm me-2" />Cancelling...</> : 'Yes, Cancel It'}
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default BookingCancelPage;